import { lazy, Suspense, useEffect, useState } from "react";
import { HashRouter, Link, Outlet, Route, Routes, useNavigate } from "react-router";
import { createDraftFromImage } from "@/data/attachments";
import { processImage } from "@/integrations/images/process-image";
import { UpdatePrompt } from "@/pwa/UpdatePrompt";
import { Notice } from "@/ui/primitives";
import { AppShell, type CaptureImageHandler } from "@/ui/shell";
import { BalancePage } from "./pages/BalancePage";
import { BudgetPage } from "./pages/BudgetPage";
import { CategoriesPage } from "./pages/CategoriesPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ExpenseFormPage } from "./pages/ExpenseFormPage";
import { ExpensesPage } from "./pages/ExpensesPage";
import { IncomeFormPage, IncomeListPage } from "./pages/IncomePages";
import { MonthPage } from "./pages/MonthPage";
import { YearPage } from "./pages/YearPage";
import { db } from "./hooks";
import { initialiseApp, watchDatabase } from "./startup";

// Occasional screens (workbook reader, sync packages, backups) load on demand; all are precached
// by the service worker, so they still work offline.
const ImportPage = lazy(() => import("./pages/ImportPage").then((m) => ({ default: m.ImportPage })));
const SyncPage = lazy(() => import("./pages/SyncPage").then((m) => ({ default: m.SyncPage })));
const ExcelCopyPage = lazy(() => import("./pages/ExcelCopyPage").then((m) => ({ default: m.ExcelCopyPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));

function Layout() {
  const navigate = useNavigate();
  // "Fotografa ricevuta" / "Carica screenshot / foto": the image is processed and stored locally
  // with a new draft, then the user completes the expense on the form.
  const captureImage: CaptureImageHandler = async (file, origin) => {
    const { draft } = await createDraftFromImage(db, file, origin, processImage);
    navigate(`/spese/nuova?bozza=${draft.id}`);
  };
  return (
    <AppShell banner={<UpdatePrompt />} onCaptureImage={captureImage}>
      <Suspense fallback={<p className="text-muted">Caricamento…</p>}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function NotFound() {
  return (
    <div>
      <h1 className="font-ledger text-3xl text-petrol-strong">Pagina non trovata</h1>
      <Link to="/" className="mt-4 inline-block font-medium text-petrol underline underline-offset-4">
        Torna al riepilogo
      </Link>
    </div>
  );
}

type DatabaseState = "opening" | "ready" | "blocked" | "slow" | "outdated";
/** Opening the database normally takes well under a second; after this, explain the wait. */
const SLOW_OPEN_MS = 8000;

/** Shown instead of an endless "Caricamento…" when the local data cannot be opened yet. */
function DatabaseNotice({ state }: { state: DatabaseState }) {
  if (state === "opening" || state === "ready") return null;
  const text =
    state === "blocked"
      ? "Aggiornamento dei dati in attesa: un'altra copia dell'app è ancora aperta con la versione precedente (l'app installata o un'altra scheda del browser). Chiudila del tutto, anche dall'elenco delle app recenti: l'aggiornamento si completa da solo."
      : state === "outdated"
        ? "L'app è stata aggiornata in un'altra finestra. Ricarica per continuare con la nuova versione."
        : "L'app non riesce ancora ad aprire i dati sul dispositivo. Chiudi le altre copie dell'app aperte (app installata, altre schede del browser) e riprova.";
  return (
    <div role="alert" className="fixed inset-x-0 top-0 z-30 border-b border-over/40 bg-over-soft px-4 py-3 text-sm shadow-sm" data-testid="database-notice">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
        <p>
          {text} <strong>I tuoi dati non vengono toccati.</strong>
        </p>
        <button type="button" onClick={() => window.location.reload()} className="rounded-lg bg-petrol px-3 py-2 font-semibold text-white">
          {state === "outdated" ? "Ricarica" : "Riprova"}
        </button>
      </div>
    </div>
  );
}

export function App() {
  const [startupError, setStartupError] = useState<string | null>(null);
  const [databaseState, setDatabaseState] = useState<DatabaseState>("opening");
  useEffect(() => {
    const stopWatching = watchDatabase({ onBlocked: () => setDatabaseState("blocked"), onOutdated: () => setDatabaseState("outdated") });
    const slow = window.setTimeout(() => setDatabaseState((s) => (s === "opening" ? "slow" : s)), SLOW_OPEN_MS);
    initialiseApp()
      // Opened (also after waiting for another copy to close): the notice goes away by itself.
      .then(() => setDatabaseState((s) => (s === "outdated" ? s : "ready")))
      .catch((e: unknown) => setStartupError((e as Error).message))
      .finally(() => window.clearTimeout(slow));
    return () => {
      stopWatching();
      window.clearTimeout(slow);
    };
  }, []);

  if (startupError) {
    return (
      <main className="mx-auto max-w-md p-6">
        <Notice tone="error">
          Impossibile aprire il database locale: {startupError}. Il browser potrebbe essere in modalità privata o avere l&apos;archiviazione disattivata.
        </Notice>
      </main>
    );
  }

  // Hash routing: works from any static host and offline, with no server rewrites.
  return (
    <HashRouter>
      <DatabaseNotice state={databaseState} />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="mese/:ym?" element={<MonthPage />} />
          <Route path="anno/:year?" element={<YearPage />} />
          <Route path="saldo" element={<BalancePage />} />
          <Route path="budget/:ym?" element={<BudgetPage />} />
          <Route path="spese" element={<ExpensesPage />} />
          <Route path="spese/nuova" element={<ExpenseFormPage />} />
          <Route path="spese/:id" element={<ExpenseFormPage />} />
          <Route path="entrate" element={<IncomeListPage />} />
          <Route path="entrate/nuova" element={<IncomeFormPage />} />
          <Route path="entrate/:id" element={<IncomeFormPage />} />
          <Route path="categorie" element={<CategoriesPage />} />
          <Route path="importa" element={<ImportPage />} />
          <Route path="sync" element={<SyncPage />} />
          <Route path="excel" element={<ExcelCopyPage />} />
          <Route path="impostazioni" element={<SettingsPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}
